/**
 * Expanded row configuration for service configoptions
 *
 * Usage:
 * ```tsx
 * // Import from templates for registry functions (avoids circular deps)
 * import { createSmartExpandedRow } from '@/modules/Service/utils/templates'
 *
 * const smartExpandedRow = useMemo(() => createSmartExpandedRow(t), [t])
 *
 * <DataTableCustomized
 *   expandable={{
 *     expandedRowRender: smartExpandedRow.render,
 *     rowExpandable: smartExpandedRow.isExpandable,
 *   }}
 * />
 * ```
 */
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { createExpandedRowColumns } from './columns'
import { createExpandedRowData } from './data'
import { shouldDisplayValue } from './utils'

// Re-export types
export type {
    TemplateConfigField,
    FieldValueResult,
    ExpandedRowMetadata,
} from './types'

// NOTE: Template-related functions (createSmartExpandedRow, getTemplateConfig, etc.)
// should be imported directly from '@/modules/Service/utils/templates'
// to avoid circular dependencies

// Re-export legacy constants (backward compatibility)
export { EXCLUDED_ADDON_STATUSES, isAddonActiveForSummary } from './constants'

// Re-export columns
export {
    createExpandedRowColumns,
    createExpandedRowColumnsByTemplate,
    createConfigOptionsColumns,
    createConfigOptionsColumnsByTemplate,
} from './columns'

// Re-export data
export { createExpandedRowData } from './data'

// Re-export utils
export {
    calculateFieldValue,
    calculateRowPrice,
    shouldDisplayValue,
    hasDisplayableValue,
} from './utils'

/**
 * Check if any row has displayable value for a field
 */
const hasFieldValue = (data: any[], fieldKey: string): boolean => {
    return data.some((row) => {
        const fieldData = row[`_field_${fieldKey}`]
        if (!fieldData) return false
        // For server field, check modelName
        if (fieldKey === 'server') return !!fieldData.modelName
        return shouldDisplayValue(fieldData.value)
    })
}

/** TemplateField type for getTemplateFields parameter */
export interface TemplateFieldDef {
    key: string
    names: string[]
    label: string
    headerKey?: string
}

/** Type for getTemplateFields function */
export type GetTemplateFieldsFn = (templateName?: string) => TemplateFieldDef[]

/**
 * Create expandedRowRender function for dynamic template-specific columns
 * (Default expanded row renderer - used by most templates)
 * @param t - Translation function
 * @param getTemplateFields - Optional function to get template fields (passed to avoid circular deps)
 */
export const createExpandedRowRender = (
    t: TFunction,
    getTemplateFields?: GetTemplateFieldsFn
) => {
    return (row: any, _index: number): ReactNode => {
        const serviceTemplate = row?.service_template
        const allColumns = createExpandedRowColumns(t, serviceTemplate)
        const data = createExpandedRowData(row, t)

        if (data.length === 0) {
            return (
                <div className="text-gray-500 text-sm p-4">
                    {t('no-config-options', {
                        defaultValue: 'No configuration options',
                    })}
                </div>
            )
        }

        // Get field keys from template to identify field columns
        const templateFields = getTemplateFields?.(serviceTemplate) || []
        const fieldKeys = new Set(templateFields.map((f) => f.key))

        // Filter columns: keep non-field columns + field columns with displayable values
        const columns = allColumns.filter((col) => {
            const key = col.key as string
            // Keep non-field columns (source, pricing_model, price_recurring)
            if (!fieldKeys.has(key)) return true
            // Only keep field columns if any row has displayable value
            return hasFieldValue(data, key)
        })

        return (
            <DataTableCustomized
                columns={columns}
                rows={data}
                rowKey={(item) =>
                    item._id ||
                    `${item._isAddon ? 'addon' : 'main'}-${item.service_id || item.addon_service_name}`
                }
                rowClassName={(item) =>
                    item._isSummary
                        ? 'border-t-2 border-primary/20 [&>td]:!bg-primary/5'
                        : ''
                }
                size="small"
                stickyHeader={{ enabled: false }}
            />
        )
    }
}
